import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Info, AlertTriangle } from 'lucide-react';
import type {
  EditorProps,
  HeroCarouselConfig,
  SocialMediaFeedConfig,
  SocialPlatform,
} from './types';

const HANDLE_RE = /^[A-Za-z0-9._-]{1,50}$/;
const HERO_IMAGE_RE = /^(https?:\/\/|\/).+/;

const HERO_DEFAULTS: HeroCarouselConfig = {
  useHeroImageAsFirstSlide: true,
  autoplay: true,
  intervalSeconds: 6,
  showCaptions: true,
};

export function SocialMediaFeedEditor({
  config,
  onChange,
}: EditorProps<SocialMediaFeedConfig>) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const update = (patch: Partial<SocialMediaFeedConfig>) => {
    onChange({ ...config, ...patch });
  };

  const hero: HeroCarouselConfig = { ...HERO_DEFAULTS, ...(config.heroCarousel ?? {}) };
  const updateHero = (patch: Partial<HeroCarouselConfig>) => {
    onChange({ ...config, heroCarousel: { ...hero, ...patch } });
  };

  const showInHero = config.showInHero !== false;
  const showAsGrid = config.showAsGrid !== false;

  const handleError = useMemo(() => {
    if (!config.handle) return 'Handle ist erforderlich.';
    if (!HANDLE_RE.test(config.handle))
      return 'Nur Buchstaben, Ziffern, Punkt, Unter- und Bindestrich (max. 50 Zeichen).';
    return null;
  }, [config.handle]);

  const heroImageError = useMemo(() => {
    const url = hero.heroImageUrl?.trim();
    if (!url) return null;
    if (!HERO_IMAGE_RE.test(url))
      return 'Nur vollständige http(s)-URLs oder absolute Pfade (mit / beginnend).';
    return null;
  }, [hero.heroImageUrl]);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-neutral-300">Plattform</label>
        <select
          value={config.platform}
          onChange={(e) => update({ platform: e.target.value as SocialPlatform })}
          className="w-full rounded-lg border border-surface-400 bg-surface-200 px-3 py-2 text-sm text-neutral-200 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
        </select>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-neutral-300">
          Handle <span className="text-neutral-500">(ohne @)</span>
        </label>
        <input
          type="text"
          value={config.handle}
          onChange={(e) => update({ handle: e.target.value.trim() })}
          placeholder="z.B. albertobikes"
          maxLength={50}
          className="w-full rounded-lg border border-surface-400 bg-surface-200 px-3 py-2 text-sm text-neutral-200 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        {handleError && (
          <p className="flex items-center gap-1 text-xs text-yellow-400">
            <AlertTriangle className="h-3 w-3" />
            {handleError}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-neutral-300">Anzahl Posts</label>
          <span className="text-xs font-mono text-neutral-400">{config.count}</span>
        </div>
        <input
          type="range"
          min={1}
          max={20}
          value={config.count}
          onChange={(e) => update({ count: Number(e.target.value) })}
          className="w-full accent-brand-500"
        />
        <div className="flex justify-between text-[10px] text-neutral-500">
          <span>1</span>
          <span>20</span>
        </div>
      </div>

      {/* ─── Ansichten ──────────────────────────────────────── */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-neutral-300">Ansichten</label>
        <div className="space-y-2 rounded-lg border border-surface-300 bg-surface-100 p-3">
          <Toggle
            label="Hero-Karussell"
            description="Slideshow im Hero-Bereich der Seite"
            checked={showInHero}
            onChange={(v) => update({ showInHero: v })}
          />
          <Toggle
            label="Raster-Ansicht"
            description="Posts als Grid im News-Bereich"
            checked={showAsGrid}
            onChange={(v) => update({ showAsGrid: v })}
          />
        </div>
      </div>

      {/* ─── Hero-Karussell-Optionen ─────────────────────────── */}
      {showInHero && (
        <div className="space-y-4 rounded-lg border border-surface-300 bg-surface-100 p-3">
          <Toggle
            label="Hero-Bild als ersten Slide"
            description="Das bestehende Hero-Bild der Seite wird dem Feed vorangestellt."
            checked={hero.useHeroImageAsFirstSlide}
            onChange={(v) => updateHero({ useHeroImageAsFirstSlide: v })}
          />

          {hero.useHeroImageAsFirstSlide && (
            <div className="space-y-1.5 pl-1">
              <label className="text-xs font-medium text-neutral-300">
                Hero-Bild URL <span className="text-neutral-500">(optional)</span>
              </label>
              <input
                type="text"
                value={hero.heroImageUrl ?? ''}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  updateHero({ heroImageUrl: v === '' ? undefined : v });
                }}
                placeholder="Leer = Hero-Bild aus dem Template"
                maxLength={2048}
                className="w-full rounded-lg border border-surface-400 bg-surface-200 px-3 py-2 text-sm text-neutral-200 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              {heroImageError ? (
                <p className="flex items-center gap-1 text-xs text-yellow-400">
                  <AlertTriangle className="h-3 w-3" />
                  {heroImageError}
                </p>
              ) : (
                <p className="text-[11px] text-neutral-500">
                  Leer lassen, um das im Template hinterlegte Hero-Bild zu verwenden.
                </p>
              )}
            </div>
          )}

          <Toggle
            label="Automatisch weiterschalten"
            description="Slides wechseln selbstständig im eingestellten Intervall."
            checked={hero.autoplay}
            onChange={(v) => updateHero({ autoplay: v })}
          />

          {hero.autoplay && (
            <div className="space-y-1.5 pl-1">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-neutral-300">
                  Intervall (Sekunden)
                </label>
                <span className="text-xs font-mono text-neutral-400">{hero.intervalSeconds}s</span>
              </div>
              <input
                type="range"
                min={2}
                max={30}
                value={hero.intervalSeconds}
                onChange={(e) => updateHero({ intervalSeconds: Number(e.target.value) })}
                className="w-full accent-brand-500"
              />
              <div className="flex justify-between text-[10px] text-neutral-500">
                <span>2s</span>
                <span>30s</span>
              </div>
            </div>
          )}

          <Toggle
            label="Bildunterschriften anzeigen"
            description="Caption-Text der Posts über dem Bild einblenden."
            checked={hero.showCaptions}
            onChange={(v) => updateHero({ showCaptions: v })}
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => setAdvancedOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-200"
      >
        {advancedOpen ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        Erweitert
      </button>
      {advancedOpen && (
        <div className="space-y-1.5 rounded-lg border border-surface-300 bg-surface-100 p-3">
          <label className="text-xs font-medium text-neutral-300">
            Refresh-Intervall (Minuten)
          </label>
          <input
            type="number"
            min={15}
            max={1440}
            value={config.refreshIntervalMinutes ?? 60}
            onChange={(e) =>
              update({ refreshIntervalMinutes: Number(e.target.value) || 60 })
            }
            className="w-full rounded-lg border border-surface-400 bg-surface-200 px-3 py-2 text-sm text-neutral-200 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <p className="text-[11px] text-neutral-500">Zwischen 15 und 1440 Minuten.</p>
        </div>
      )}

      <div className="flex items-start gap-2 rounded-lg border border-brand-900/50 bg-brand-900/10 p-3 text-xs text-brand-200">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <p>
          Posts werden alle {config.refreshIntervalMinutes ?? 60} Minuten vom Meta Proxy
          gecacht. Der API-Key wird serverseitig verwaltet.
        </p>
      </div>
    </div>
  );
}

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-medium text-neutral-200">{label}</p>
        {description && <p className="mt-0.5 text-[11px] text-neutral-500">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-brand-500' : 'bg-surface-400'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}
